import React, { useContext, useState } from "react";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import bkstApi from "../../api";
import EmailInput from "../../common/component/EmailInput";
import CloudinaryUploadWidget from "../../common/component/CloudinaryUploadWidget";
import { UserContext } from "../../context/UserContext";

function formatRoleLabel(role) {
  return (role || "")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export default function EditStaff(props) {
  const { staff: member, onSaved } = props;
  const { user } = useContext(UserContext);
  const storeLocations = user?.config?.storeLocation || [];
  const showStoreLocations =
    user?.capabilities?.includes("multi-location") && storeLocations.length > 1;
  const showLocationPicker = showStoreLocations;

  const [open, setOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [errors, setErrors] = useState([]);
  const [confirmArchive, setConfirmArchive] = useState(false);

  const [photo, setPhoto] = useState("");
  const [photoError, setPhotoError] = useState(false);
  const [accessCode, setAccessCode] = useState("");
  const [storeLocationIds, setStoreLocationIds] = useState([]);

  const canArchive = member.uid !== user?.uid;

  const handleOpen = () => {
    setPhoto(member.photo || "");
    setPhotoError(false);
    setAccessCode(member.accessCode || "");
    setStoreLocationIds(Array.isArray(member.storeLocationIds) ? member.storeLocationIds : []);
    setErrors([]);
    setConfirmArchive(false);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setErrors([]);
    setConfirmArchive(false);
  };

  const handleAccessCodeChange = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 4);
    setAccessCode(digits);
  };

  const photoSrc =
    photo ||
    (member.uid
      ? `https://storage.googleapis.com/bakestreet-f55a0.appspot.com/img/u/${member.uid}.webp`
      : "");

  const validate = () => {
    const arr = [];
    if (accessCode && accessCode.length !== 4) {
      arr.push("Access code must be 4 digits");
    }
    if (member.role === "clerk" && accessCode.length !== 4) {
      arr.push("Access code is required for clerk role");
    }
    return arr;
  };

  const submit = (e) => {
    e.preventDefault();
    const arr = validate();
    setErrors(arr);
    if (arr.length > 0) {
      return;
    }

    setProcessing(true);
    bkstApi
      .put(`/my/staff/${member.uid}`, {
        photo,
        accessCode,
        ...(showLocationPicker ? { storeLocationIds } : {}),
      })
      .then((res) => {
        setProcessing(false);
        handleClose();
        if (onSaved) {
          onSaved(res.data.staff);
        }
      })
      .catch((err) => {
        setProcessing(false);
        setErrors([err.response?.data?.message || "Failed to update staff"]);
      });
  };

  const archive = () => {
    setProcessing(true);
    setConfirmArchive(false);
    bkstApi
      .post(`/my/staff/${member.uid}/archive`)
      .then(() => {
        setProcessing(false);
        handleClose();
        if (onSaved) {
          onSaved();
        }
      })
      .catch((err) => {
        setProcessing(false);
        setErrors([err.response?.data?.message || "Failed to deactivate account"]);
      });
  };

  return (
    <>
      <IconButton size="small" onClick={handleOpen} aria-label="Edit staff">
        <EditIcon fontSize="small" />
      </IconButton>
      <Dialog fullScreen={true} open={open} scroll="paper">
        <form onSubmit={submit} style={{ display: "contents" }}>
          <DialogTitle
            sx={{
              padding: "7px 20px",
              display: "flex",
              gap: "10px",
              alignItems: "center",
              margin: "0",
              backgroundColor: "#DEDEDE",
              borderBottom: "3px solid #DEDEDE",
            }}
          >
            <IconButton onClick={handleClose}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5">Edit Staff</Typography>
          </DialogTitle>
          <DialogContent sx={{ backgroundColor: "#F7F7F7" }}>
            <Container maxWidth="md">
              {errors.length > 0 && (
                <Box my={2}>
                  <Alert severity="error">{errors.join(". ")}</Alert>
                </Box>
              )}
              <Box py={4}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={3} md={2}>
                    <Box>
                      <Box
                        sx={{
                          width: "100%",
                          aspectRatio: "1/1",
                          backgroundColor: "#ECECEC",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          overflow: "hidden",
                          mb: 1,
                        }}
                      >
                        {photoSrc && !photoError ? (
                          <img
                            src={photoSrc}
                            alt=""
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            onError={() => setPhotoError(true)}
                          />
                        ) : null}
                      </Box>
                      <CloudinaryUploadWidget
                        folder={`s/${user.acctId}/staff`}
                        onComplete={(images) => {
                          setPhotoError(false);
                          setPhoto(images?.[0] || "");
                        }}
                      >
                        <Button variant="contained" color="secondary" fullWidth>
                          Upload
                        </Button>
                      </CloudinaryUploadWidget>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={9} md={10}>
                    <Paper elevation={0}>
                      <Box px={2} py={4}>
                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              value={member.firstName || ""}
                              variant="outlined"
                              fullWidth
                              label="First Name"
                              disabled
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              value={member.lastName || ""}
                              variant="outlined"
                              fullWidth
                              label="Last Name"
                              disabled
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <FormControl fullWidth disabled>
                              <InputLabel id="edit-staff-role-label">Role</InputLabel>
                              <Select
                                labelId="edit-staff-role-label"
                                value={member.role || ""}
                                label="Role"
                              >
                                <MenuItem value={member.role || ""}>
                                  {formatRoleLabel(member.role)}
                                </MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          {member.role !== "clerk" && member.role !== "kitchen" && (
                            <Grid item xs={12}>
                              <EmailInput value={member.email || ""} onChange={() => {}} disabled />
                            </Grid>
                          )}
                          <Grid item xs={12}>
                            <TextField
                              value={accessCode}
                              variant="outlined"
                              fullWidth
                              label="Access Code"
                              placeholder="Access Code"
                              inputProps={{
                                inputMode: "numeric",
                                pattern: "[0-9]*",
                                maxLength: 4,
                              }}
                              onChange={(e) => handleAccessCodeChange(e.target.value)}
                              required={member.role === "clerk"}
                              helperText={
                                member.role === "clerk"
                                  ? "Required 4-digit code for login"
                                  : "Optional 4-digit code for POS / KDS login"
                              }
                            />
                          </Grid>
                          {showLocationPicker && (
                            <Grid item xs={12}>
                              <FormControl fullWidth>
                                <InputLabel id="edit-staff-locations-label">
                                  Store Locations
                                </InputLabel>
                                <Select
                                  labelId="edit-staff-locations-label"
                                  multiple
                                  value={storeLocationIds}
                                  onChange={(e) => setStoreLocationIds(e.target.value)}
                                  input={<OutlinedInput label="Store Locations" />}
                                  renderValue={(selected) =>
                                    storeLocations
                                      .filter((loc) => selected.includes(loc.id))
                                      .map((loc) => loc.address?.street)
                                      .join(", ")
                                  }
                                >
                                  {storeLocations.map((loc) => (
                                    <MenuItem key={loc.id} value={loc.id}>
                                      <Checkbox checked={storeLocationIds.includes(loc.id)} />
                                      <ListItemText primary={loc.address?.street || loc.id} />
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    </Paper>
                    <Box my={2}>
                      <Button type="submit" fullWidth color="primary" variant="contained">
                        SAVE
                      </Button>
                    </Box>
                    {canArchive && (
                      <Box mt={4} textAlign="center">
                        <Button
                          color="error"
                          variant="text"
                          onClick={() => setConfirmArchive(true)}
                        >
                          Deactivate Account
                        </Button>
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </Box>
            </Container>
          </DialogContent>
        </form>
        {processing && (
          <Backdrop open={true} style={{ zIndex: "1000", color: "#fff" }}>
            <CircularProgress color="inherit" />
          </Backdrop>
        )}
      </Dialog>
      <Dialog open={confirmArchive} onClose={() => setConfirmArchive(false)}>
        <DialogTitle>Deactivate account?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Deactivate {member.firstName} {member.lastName}? They will be marked inactive and will
            no longer be able to sign in.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmArchive(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={archive}>
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
